import { z } from 'zod';
import { ErrorCodeSchema } from './errors.js';
import {
  ActionSchema,
  GameIdSchema,
  PieceIdSchema,
  TeamSchema,
  WinReasonSchema,
} from './primitives.js';
import { ClocksSchema, TimeControlSchema } from './time.js';

export const PlayerInfoSchema = z
  .object({
    id: z.string().min(1).max(64),
    name: z.string().min(1).max(40),
    guest: z.boolean(),
    rating: z.number().nullable(),
  })
  .strict();
export type PlayerInfo = z.infer<typeof PlayerInfoSchema>;

export const RoleSchema = z.enum(['I', 'B', 'spectator']);
export type Role = z.infer<typeof RoleSchema>;

const hash = z.string().regex(/^[0-9a-f]{8}$/);
const ply = z.number().int().min(0);

/** Full state of a game, sent on join and on reconnect. */
export const GameSnapshotSchema = z
  .object({
    type: z.literal('game.snapshot'),
    gameId: GameIdSchema,
    role: RoleSchema,
    players: z.object({ I: PlayerInfoSchema.nullable(), B: PlayerInfoSchema.nullable() }).strict(),
    timeControl: TimeControlSchema,
    clocks: ClocksSchema,
    /** The game so far as a serialised record (see `@halvard/rules` `serialize`). */
    record: z.string().max(64 * 1024),
    /** `stateHash` of the state the record replays to. */
    hash,
    connected: z.object({ I: z.boolean(), B: z.boolean() }).strict(),
    drawOffer: TeamSchema.nullable(),
    rematchOffer: z.object({ by: TeamSchema, sameMountain: z.boolean() }).strict().nullable(),
  })
  .strict();
export type GameSnapshot = z.infer<typeof GameSnapshotSchema>;

/** One validated action, broadcast to players and spectators. */
export const GameActionEventSchema = z
  .object({
    type: z.literal('game.action'),
    gameId: GameIdSchema,
    /** Ply at which the action was played (the client's `seq`). */
    ply,
    pieceId: PieceIdSchema,
    action: ActionSchema,
    notation: z.string().max(32),
    /** `stateHash` after the action. */
    hash,
    clocks: ClocksSchema,
  })
  .strict();
export type GameActionEvent = z.infer<typeof GameActionEventSchema>;

/** Every message the server may send. */
export const ServerMessageSchema = z.discriminatedUnion('type', [
  z
    .object({
      type: z.literal('welcome'),
      protocolVersion: z.number().int(),
      rulesVersion: z.string(),
      player: PlayerInfoSchema,
      /** A new guest token to store, when the client connected without one. */
      guestToken: z.string().optional(),
    })
    .strict(),
  z.object({ type: z.literal('pong'), nonce: z.number().int() }).strict(),
  z
    .object({
      type: z.literal('error'),
      code: ErrorCodeSchema,
      message: z.string(),
      gameId: GameIdSchema.optional(),
      seq: ply.optional(),
    })
    .strict(),

  z.object({ type: z.literal('queue.waiting'), timeControl: TimeControlSchema }).strict(),
  z.object({ type: z.literal('queue.left') }).strict(),

  z
    .object({ type: z.literal('game.created'), gameId: GameIdSchema, joinToken: z.string() })
    .strict(),
  GameSnapshotSchema,
  GameActionEventSchema,
  /**
   * Reserved for the fog-of-war variant: the opponent acted, but what they did is hidden.
   * Followed by a fresh snapshot of what the receiving player can see.
   */
  z
    .object({
      type: z.literal('game.hidden-action'),
      gameId: GameIdSchema,
      ply,
      team: TeamSchema,
      clocks: ClocksSchema,
    })
    .strict(),
  z
    .object({
      type: z.literal('game.over'),
      gameId: GameIdSchema,
      winner: TeamSchema.nullable(),
      reason: WinReasonSchema,
      hash,
      clocks: ClocksSchema,
    })
    .strict(),
  z
    .object({
      type: z.literal('player.connection'),
      gameId: GameIdSchema,
      team: TeamSchema,
      connected: z.boolean(),
      /** Time left to reconnect before the game is lost, when disconnected. */
      graceMs: z.number().int().min(0).optional(),
    })
    .strict(),

  z.object({ type: z.literal('draw.offered'), gameId: GameIdSchema, by: TeamSchema }).strict(),
  z.object({ type: z.literal('draw.declined'), gameId: GameIdSchema, by: TeamSchema }).strict(),
  z
    .object({
      type: z.literal('rematch.offered'),
      gameId: GameIdSchema,
      by: TeamSchema,
      sameMountain: z.boolean(),
    })
    .strict(),
  z.object({ type: z.literal('rematch.declined'), gameId: GameIdSchema, by: TeamSchema }).strict(),
  z
    .object({ type: z.literal('rematch.started'), gameId: GameIdSchema, newGameId: GameIdSchema })
    .strict(),
]);

export type ServerMessage = z.infer<typeof ServerMessageSchema>;
export type ServerMessageType = ServerMessage['type'];
export type ServerMessageOf<T extends ServerMessageType> = Extract<ServerMessage, { type: T }>;
